import { Link, NavLink, Outlet, useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronRight, LayoutDashboard, LogOut, User, Users } from 'lucide-react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useMe } from '@/hooks/useProfile'
import { clearToken } from '@/lib/api'
import { avatarInitials } from '@/lib/format'
import { cn } from '@/lib/utils'

const NAV_ITEMS = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/users', label: 'Users', icon: Users, end: false },
]

/** Layout for every signed-in admin page: fixed sidebar + content. Two nav
 * items only, so no collapse/drawer behavior — unlike frontend/'s AppShell,
 * which manages that complexity for a much larger nav tree. */
export function AppShell() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: me } = useMe()

  function signOut() {
    clearToken()
    queryClient.clear()
    void navigate('/login', { replace: true })
  }

  return (
    <div className="flex h-svh overflow-hidden">
      <aside className="bg-sidebar flex w-64 shrink-0 flex-col border-r">
        <div className="flex items-center gap-2.5 p-4">
          <img src="/logo.png" alt="" className="size-9 shrink-0 rounded-lg" />
          <div className="min-w-0">
            <span className="font-heading block truncate text-base font-semibold">
              Statement AI
            </span>
            <span className="text-muted-foreground block truncate text-sm">Admin</span>
          </div>
        </div>

        <nav className="flex-1 space-y-1.5 px-3">
          {NAV_ITEMS.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors',
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                )
              }
            >
              <Icon className="size-5" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t p-3">
          <DropdownMenu>
            <DropdownMenuTrigger className="hover:bg-sidebar-accent flex w-full items-center gap-3 rounded-lg p-2.5 text-left">
              <span className="bg-primary text-primary-foreground flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                {avatarInitials(me?.name)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold">{me?.name ?? 'Loading…'}</span>
                <span className="text-muted-foreground block truncate text-xs">Admin</span>
              </span>
              <ChevronRight className="text-muted-foreground size-4 shrink-0" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuItem asChild>
                <Link to="/profile">
                  <User className="size-4" /> Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={signOut}>
                <LogOut className="size-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  )
}
